import * as React from "react"
import { ArrowLeft, X } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet"

const NESTED_PORTAL_SELECTOR = [
  "[data-radix-popper-content-wrapper]",
  "[data-radix-select-content]",
  "[data-radix-menu-content]",
  "[data-radix-dropdown-menu-content]",
  "[data-radix-popover-content]",
  "[role='listbox']",
  "[data-radix-dialog-content]:not([data-floating-drawer-panel])",
  // 🔴 THE DATE RANGE CALENDAR IS NOT A RADIX POPOVER.
  //
  // It portals itself to document.body with its own markers rather than
  // through Radix, so none of the selectors above match it. Without these two
  // entries, clicking a date inside the calendar reads as a click outside the
  // drawer and closes the whole drawer mid-selection — the calendar's own
  // month dropdown (`caption-menu`) does the same.
  "[data-date-range-panel]",
  "[data-date-range-caption-menu]",
].join(",")

function isNestedPortaledUi(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return !!target.closest(NESTED_PORTAL_SELECTOR)
}

function hasOpenNestedPortaledUi(): boolean {
  if (typeof document === "undefined") return false
  return !!document.querySelector(NESTED_PORTAL_SELECTOR)
}

const NESTED_DISMISS_GRACE_MS = 400

export const floatingDrawerFileInputCls =
  "absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"

export interface FloatingDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  side?: "left" | "right"
  width?: number
  title?: string
  className?: string
  children: React.ReactNode
  resizeHandle?: React.ReactNode
}

export function FloatingDrawer({
  open,
  onOpenChange,
  side = "right",
  width = 480,
  title,
  className,
  children,
  resizeHandle,
}: FloatingDrawerProps) {
  const nestedDismissGraceUntilRef = React.useRef(0)

  const markNestedDismissGrace = React.useCallback(() => {
    nestedDismissGraceUntilRef.current = performance.now() + NESTED_DISMISS_GRACE_MS
  }, [])

  const isInNestedDismissGrace = React.useCallback(
    () => performance.now() < nestedDismissGraceUntilRef.current,
    []
  )

  const requestOpenChange = React.useCallback(
    (next: boolean) => {
      if (
        !next &&
        (isInNestedDismissGrace() || hasOpenNestedPortaledUi())
      ) {
        return
      }
      onOpenChange(next)
    },
    [onOpenChange, isInNestedDismissGrace]
  )

  React.useEffect(() => {
    if (!open) {
      nestedDismissGraceUntilRef.current = 0
      return
    }

    const onPointerDownCapture = (e: PointerEvent) => {
      const target = e.target
      if (!(target instanceof Element)) return

      if (isNestedPortaledUi(target) || hasOpenNestedPortaledUi()) {
        markNestedDismissGrace()
      }

      if (!target.closest("[data-floating-drawer-overlay]")) return

      if (
        isInNestedDismissGrace() ||
        isNestedPortaledUi(target) ||
        hasOpenNestedPortaledUi()
      ) {
        return
      }

      e.preventDefault()
      e.stopPropagation()
      requestOpenChange(false)
    }

    document.addEventListener("pointerdown", onPointerDownCapture, true)
    return () => document.removeEventListener("pointerdown", onPointerDownCapture, true)
  }, [open, requestOpenChange, markNestedDismissGrace, isInNestedDismissGrace])

  React.useEffect(() => {
    if (!open) return

    const resetDocumentScroll = () => {
      window.scrollTo(0, 0)
      const root = document.scrollingElement
      if (root) root.scrollTop = 0
      if (document.body.scrollTop) document.body.scrollTop = 0
    }

    const onWindowFocus = () => {
      resetDocumentScroll()
      requestAnimationFrame(resetDocumentScroll)
    }

    window.addEventListener("focus", onWindowFocus)
    return () => window.removeEventListener("focus", onWindowFocus)
  }, [open])

  return (
    <Sheet open={open} onOpenChange={requestOpenChange}>
      <SheetContent
        floating
        side={side}
        hideClose
        className={cn("p-0 gap-0", className)}
        style={{ width }}
        onPointerDownOutside={(e) => {
          if (isNestedPortaledUi(e.target)) {
            e.preventDefault()
            return
          }
          if (hasOpenNestedPortaledUi() || isInNestedDismissGrace()) {
            markNestedDismissGrace()
            return
          }
          requestOpenChange(false)
        }}
        onInteractOutside={(e) => {
          if (isNestedPortaledUi(e.target)) {
            e.preventDefault()
            return
          }
          if (hasOpenNestedPortaledUi() || isInNestedDismissGrace()) {
            markNestedDismissGrace()
          }
        }}
        onFocusOutside={(e) => {
          if (
            isInNestedDismissGrace() ||
            isNestedPortaledUi(e.target) ||
            hasOpenNestedPortaledUi()
          ) {
            e.preventDefault()
            return
          }
          const oe = e.detail?.originalEvent
          if (oe instanceof FocusEvent && !oe.relatedTarget) {
            markNestedDismissGrace()
            e.preventDefault()
          }
        }}
        onEscapeKeyDown={(e) => {
          if (hasOpenNestedPortaledUi()) {
            markNestedDismissGrace()
            e.preventDefault()
          }
        }}
      >
        {title ? (
          <>
            <SheetTitle className="sr-only">{title}</SheetTitle>
            <SheetDescription className="sr-only">{title}</SheetDescription>
          </>
        ) : null}
        {resizeHandle}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export function FloatingDrawerHeader({
  title,
  subtitle,
  icon,
  onBack,
  right,
  className,
}: {
  title: React.ReactNode
  subtitle?: React.ReactNode
  icon?: React.ReactNode
  onBack?: () => void
  right?: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "z-10 flex shrink-0 items-center gap-3 rounded-t-2xl border-b border-[#EAECF0] bg-white/95 px-5 py-4 backdrop-blur",
        className
      )}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F2F4F7] text-[#475467] transition-colors hover:bg-[#E4E7EC] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      ) : icon ? (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F2F4F7] text-[#475467]">
          {icon}
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <SheetTitle className="truncate text-[16px] font-semibold leading-tight text-[#101828]">
          {title}
        </SheetTitle>
        {subtitle ? (
          <SheetDescription
            asChild
            className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12px] font-normal leading-snug text-[#667085]"
          >
            <div>{subtitle}</div>
          </SheetDescription>
        ) : null}
      </div>
      {right}
      <SheetClose
        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#475467] transition-colors hover:bg-[#F2F4F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </SheetClose>
    </div>
  )
}

export function FloatingDrawerBody({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4", className)}>
      {children}
    </div>
  )
}

export function FloatingDrawerFooter({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        "z-10 flex shrink-0 items-center justify-end gap-2 rounded-b-2xl border-t border-[#EAECF0] bg-white/95 px-5 py-3 backdrop-blur",
        className
      )}
    >
      {children}
    </div>
  )
}
